import React from "react";
import { NavLink, useMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@contexts/AuthContext";
import { useConfig } from "@contexts/ConfigContext";
import { useThemeMode } from "@contexts/ThemeContext";
import { useIsDrawerViewport } from "@/hooks/useIsPhoneViewport";
import { Tooltip } from "@fluentui/react-components";
import { ArchiveRegular } from "@fluentui/react-icons/svg/archive";
import { ChartMultipleRegular } from "@fluentui/react-icons/svg/chart-multiple";
import { ChevronDoubleLeftRegular } from "@fluentui/react-icons/svg/chevron-double-left";
import { CalendarLtrRegular } from "@fluentui/react-icons/svg/calendar-ltr";
import { ClipboardTaskListLtrRegular } from "@fluentui/react-icons/svg/clipboard-task-list-ltr";
import { FilterRegular } from "@fluentui/react-icons/svg/filter";
import { PlugConnectedRegular } from "@fluentui/react-icons/svg/plug-connected";
import { SettingsRegular } from "@fluentui/react-icons/svg/settings";
import { SignOutRegular } from "@fluentui/react-icons/svg/sign-out";
import SidebarConnections from "../connections/SidebarConnections/SidebarConnections";
import SidebarTagFilters from "../connections/SidebarConnections/SidebarTagFilters";
import SidebarFilterReset from "./SidebarFilterReset";
import SidebarOverviewFilters from "./SidebarOverviewFilters";
import "./AppSidebar.scss";

const NON_BOARD_ROUTES = new Set(["loading", "dashboard", "regions"]);

interface AppSidebarProps {
  isCollapsed: boolean;
  isMobileOpen: boolean;
  onCollapse: () => void;
  onMobileClose: () => void;
}

// Conditionally wraps a ReactElement in a Fluent UI Tooltip (for collapsed icon-only mode)
const MaybeTooltip: React.FC<{
  show: boolean;
  content: string;
  children: React.ReactElement;
}> = ({ show, content, children }) =>
  show ? (
    <Tooltip content={content} relationship="label" positioning="after">
      {children}
    </Tooltip>
  ) : (
    children
  );

const AppSidebar: React.FC<AppSidebarProps> = ({
  isCollapsed,
  isMobileOpen,
  onCollapse,
  onMobileClose,
}) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { config } = useConfig();
  const { themeMode } = useThemeMode();
  // Navigation moved to the header, which has no room for it at drawer widths.
  // So the rail keeps it only while it *is* the drawer; on a desktop it is the
  // board's filter pane and nothing else.
  const showNavigation = useIsDrawerViewport();
  const boardMatch = useMatch("/:boardId/*");
  const rawBoardId = boardMatch?.params.boardId;
  const boardId =
    rawBoardId && !NON_BOARD_ROUTES.has(rawBoardId) ? rawBoardId : undefined;

  const tasksPath = boardId ? `/${boardId}/tasks` : "/dashboard";
  const schedulePath = boardId ? `/${boardId}/schedule` : "/dashboard";
  const numbersPath = boardId ? `/${boardId}/numbers` : "/dashboard";
  const hiddenPath = boardId ? `/${boardId}/hidden` : "/dashboard";
  const connectionsPath = boardId ? `/${boardId}/connections` : "/dashboard";
  const settingsPath = boardId ? `/${boardId}/settings` : "/dashboard";

  const sidebarClass = [
    "app-sidebar",
    isCollapsed ? "app-sidebar--collapsed" : "",
    isMobileOpen ? "app-sidebar--mobile-open" : "",
    themeMode === "dark" ? "app-sidebar--dark" : "",
    themeMode === "classic" ? "app-sidebar--brand" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      {/* Backdrop for mobile drawer */}
      {isMobileOpen && (
        <div
          className="sidebar-backdrop"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      <aside className={sidebarClass}>
        <nav
          className="sidebar-nav"
          aria-label={t("layout.sidebar.mainNavigationAria")}
        >
          {/* Mobile-only: user identity + logout */}
          {user && (
            <div className="sidebar-nav__mobile-user">
              <div className="sidebar-nav__user-name">{user.name}</div>
              <div className="sidebar-nav__user-meta">{user.email}</div>
              {config?.regionName && (
                <div className="sidebar-nav__user-meta">
                  <strong>{t("layout.userMenu.region")}</strong>{" "}
                  {config.regionName}
                </div>
              )}
              <button className="sidebar-nav__link" onClick={logout}>
                <SignOutRegular className="sidebar-nav__icon" />
                <span className="sidebar-nav__label">
                  {t("common.actions.logout")}
                </span>
              </button>
              <div className="sidebar-nav__divider" />
            </div>
          )}

          {showNavigation && (
            <>
          <MaybeTooltip show={isCollapsed} content={t("layout.tabs.tasks")}>
            <NavLink
              to={tasksPath}
              className={({ isActive }) =>
                `sidebar-nav__link${isActive ? " active" : ""}`
              }
              onClick={onMobileClose}
            >
              <ClipboardTaskListLtrRegular className="sidebar-nav__icon" />
              {!isCollapsed && (
                <>
                  <span className="sidebar-nav__label">
                    {t("layout.tabs.tasks")}
                  </span>
                </>
              )}
            </NavLink>
          </MaybeTooltip>

          {/* The board's other view of the same tasks. The toolbar toggle is
              the quick way across, but this drawer is the only navigation at
              these widths, and without an entry here nothing in it is marked
              while the schedule is open. */}
          <MaybeTooltip show={isCollapsed} content={t("layout.tabs.schedule")}>
            <NavLink
              to={schedulePath}
              className={({ isActive }) =>
                `sidebar-nav__link${isActive ? " active" : ""}`
              }
              onClick={onMobileClose}
            >
              <CalendarLtrRegular className="sidebar-nav__icon" />
              {!isCollapsed && (
                <span className="sidebar-nav__label">
                  {t("layout.tabs.schedule")}
                </span>
              )}
            </NavLink>
          </MaybeTooltip>

          {/* Third, as in the header: it is where a board gets its tasks. */}
          <MaybeTooltip
            show={isCollapsed}
            content={t("layout.tabs.connections")}
          >
            <NavLink
              to={connectionsPath}
              className={({ isActive }) =>
                `sidebar-nav__link${isActive ? " active" : ""}`
              }
              onClick={onMobileClose}
            >
              <PlugConnectedRegular className="sidebar-nav__icon" />
              {!isCollapsed && (
                <span className="sidebar-nav__label">
                  {t("layout.tabs.connections")}
                </span>
              )}
            </NavLink>
          </MaybeTooltip>

          <MaybeTooltip show={isCollapsed} content={t("layout.tabs.numbers")}>
            <NavLink
              to={numbersPath}
              className={({ isActive }) =>
                `sidebar-nav__link${isActive ? " active" : ""}`
              }
              onClick={onMobileClose}
            >
              <ChartMultipleRegular className="sidebar-nav__icon" />
              {!isCollapsed && (
                <span className="sidebar-nav__label">
                  {t("layout.tabs.numbers")}
                </span>
              )}
            </NavLink>
          </MaybeTooltip>

          <MaybeTooltip
            show={isCollapsed}
            content={t("layout.tabs.hiddenTasks")}
          >
            <NavLink
              to={hiddenPath}
              className={({ isActive }) =>
                `sidebar-nav__link${isActive ? " active" : ""}`
              }
              onClick={onMobileClose}
            >
              <ArchiveRegular className="sidebar-nav__icon" />
              {!isCollapsed && (
                <span className="sidebar-nav__label">
                  {t("layout.tabs.hiddenTasks")}
                </span>
              )}
            </NavLink>
          </MaybeTooltip>

          {/* The header's nav carries this as an icon, but the header has no
              nav at drawer widths — so the drawer keeps it too. */}
          <MaybeTooltip show={isCollapsed} content={t("layout.tabs.settings")}>
            <NavLink
              to={settingsPath}
              className={({ isActive }) =>
                `sidebar-nav__link${isActive ? " active" : ""}`
              }
              onClick={onMobileClose}
            >
              <SettingsRegular className="sidebar-nav__icon" />
              {!isCollapsed && (
                <span className="sidebar-nav__label">
                  {t("layout.tabs.settings")}
                </span>
              )}
            </NavLink>
          </MaybeTooltip>
              <div className="sidebar-nav__divider" />
            </>
          )}

          {/* Everything below is the board's filter pane. The rail stopped
              being navigation on a desktop, so it says what it is now. */}
          <div className="sidebar-nav__pane-title">
            <FilterRegular className="sidebar-nav__pane-title-icon" />
            {!isCollapsed && <span>{t("layout.sidebar.filtersTitle")}</span>}
            <SidebarFilterReset isCollapsed={isCollapsed} />
          </div>

          <SidebarOverviewFilters isCollapsed={isCollapsed} />

          <SidebarConnections
            isCollapsed={isCollapsed}
            isMobileOpen={isMobileOpen}
            onMobileClose={onMobileClose}
          />


          <SidebarTagFilters isCollapsed={isCollapsed} />

          <div className="sidebar-nav__divider" />
        </nav>

        {/* Desktop collapse toggle */}
        <button
          className="sidebar-minimizer"
          onClick={onCollapse}
          title={
            isCollapsed
              ? t("layout.sidebar.expandSidebar")
              : t("layout.sidebar.collapseSidebar")
          }
          aria-label={
            isCollapsed
              ? t("layout.sidebar.expandSidebar")
              : t("layout.sidebar.collapseSidebar")
          }
        >
          <ChevronDoubleLeftRegular
            className={`sidebar-minimizer__icon${isCollapsed ? " sidebar-minimizer__icon--rotated" : ""}`}
          />
        </button>
      </aside>
    </>
  );
};

export default AppSidebar;
